import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import SkeletonCard from "@/components/feature/SkeletonCard";
import usePageMeta from "@/hooks/usePageMeta";
import { getCategoryById, getNewsByCategory, paginate } from "@/utils/news";
import type { SortKey } from "@/types/news";

const PAGE_SIZE = 15;

export default function CategoryPage() {
  const { categoryId = "" } = useParams();
  const category = getCategoryById(categoryId);

  const [sub, setSub] = useState<string>("");
  const [sort, setSort] = useState<SortKey>("time");
  const [page, setPage] = useState(1);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSub("");
    setSort("time");
    setPage(1);
    setVisibleCount(PAGE_SIZE);
  }, [categoryId]);

  useEffect(() => {
    setLoading(true);
    const id = window.setTimeout(() => setLoading(false), 500);
    return () => window.clearTimeout(id);
  }, [categoryId, sub, sort]);

  const allList = useMemo(
    () => (category ? getNewsByCategory(category.id, sub || undefined, sort) : []),
    [category, sub, sort]
  );

  const paged = paginate(allList, page, PAGE_SIZE);
  const mobileList = allList.slice(0, visibleCount);

  usePageMeta({
    title: category ? `${category.name} - 吃瓜爆料` : "分类 - 吃瓜爆料",
    description: category
      ? `${category.name}最新资讯：${category.description} 尽在吃瓜爆料。`
      : "吃瓜爆料分类新闻列表。",
    keywords: category ? `${category.name},${category.subCategories.join(",")},吃瓜爆料` : "吃瓜爆料,分类新闻",
  });

  const handlePage = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetFilter = () => {
    setSub("");
    setSort("time");
    setPage(1);
    setVisibleCount(PAGE_SIZE);
  };

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="w-full px-4 md:px-8 py-16">
          <Breadcrumb items={[{ label: "分类不存在" }]} />
          <EmptyState
            icon="ri-error-warning-line"
            title="该分类不存在或已下线"
            description="你访问的分类可能已被调整，返回首页看看其他精彩内容吧。"
            action={
              <Link
                to="/"
                className="inline-flex items-center gap-1 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-home-4-line" /> 返回首页
              </Link>
            }
          />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="w-full px-4 md:px-8 py-6">
        <Breadcrumb items={[{ label: category.name }]} />

        <div className="rounded-lg bg-primary-50 border border-primary-100 p-5 md:p-7 mb-6">
          <h1 className="text-2xl md:text-3xl font-black text-foreground-950 flex items-center gap-3">
            <span className="w-10 h-10 rounded-md bg-primary-500 text-background-50 flex items-center justify-center">
              <i className={`${category.icon} text-xl`} />
            </span>
            {category.name}
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mt-3">{category.description}</p>
        </div>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-5">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => {
                setSub("");
                setPage(1);
                setVisibleCount(PAGE_SIZE);
              }}
              className={`h-9 px-4 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                sub === "" ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              全部
            </button>
            {category.subCategories.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  setSub(item);
                  setPage(1);
                  setVisibleCount(PAGE_SIZE);
                }}
                className={`h-9 px-4 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  sub === item ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm text-foreground-500 whitespace-nowrap">
              共 <strong className="text-primary-600">{allList.length}</strong> 条
            </span>
            <div className="flex items-center bg-background-100 rounded-full p-1">
              {(["time", "views"] as SortKey[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setSort(key);
                    setPage(1);
                  }}
                  className={`h-7 px-3.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    sort === key ? "bg-background-50 text-primary-600" : "text-foreground-600"
                  }`}
                >
                  {key === "time" ? "最新发布" : "按热度"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : allList.length === 0 ? (
          <EmptyState
            icon="ri-search-line"
            title="暂无相关内容"
            description="当前筛选条件下没有找到新闻，换个分类或重置筛选试试。"
            action={
              <button
                type="button"
                onClick={resetFilter}
                className="inline-flex items-center gap-1 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-refresh-line" /> 重置筛选
              </button>
            }
          />
        ) : (
          <>
            <div className="hidden md:flex flex-col gap-3">
              {paged.items.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>
            <div className="flex md:hidden flex-col gap-3">
              {mobileList.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>

            <Pagination page={paged.page} totalPages={paged.totalPages} onChange={handlePage} />

            {visibleCount < allList.length && (
              <div className="md:hidden mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
                  className="h-11 px-6 rounded-full bg-background-100 text-foreground-800 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-background-200"
                >
                  加载更多 <i className="ri-arrow-down-line" />
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}